
interface CompressionOptions {
  maxSizeMB?: number
  maxWidthOrHeight?: number
  useWebWorker?: boolean
  quality?: number
}

/**
 * Comprime uma imagem até o tamanho máximo especificado
 * @param file - Arquivo de imagem original
 * @param options - Opções de compressão
 * @returns Promise com o arquivo comprimido
 */
export async function compressImage(
  file: File,
  options: CompressionOptions = {}
): Promise<File> {
  const {
    maxSizeMB = 10,
    maxWidthOrHeight = 1920,
    quality = 0.8
  } = options

  // Se o arquivo já é menor que o limite, retorna o original
  if (file.size <= maxSizeMB * 1024 * 1024) {
    return file
  }

  // Se não é uma imagem, retorna o arquivo original
  if (!file.type.startsWith('image/')) {
    return file
  }

  return new Promise((resolve, reject) => {
    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')
    const img = new Image()

    img.onload = () => {
      try {
        // Calcula as dimensões mantendo aspect ratio
        let { width, height } = img
        
        if (width > maxWidthOrHeight || height > maxWidthOrHeight) {
          if (width > height) {
            height = (height * maxWidthOrHeight) / width
            width = maxWidthOrHeight
          } else {
            width = (width * maxWidthOrHeight) / height
            height = maxWidthOrHeight
          }
        }

        canvas.width = width
        canvas.height = height

        // Desenha a imagem redimensionada
        ctx?.drawImage(img, 0, 0, width, height)

        // Converte para blob com qualidade especificada
        canvas.toBlob(
          (blob) => {
            if (!blob) {
              reject(new Error('Erro ao comprimir a imagem'))
              return
            }

            // Verifica se ainda está muito grande e reduz a qualidade se necessário
            const currentQuality = quality
            const tryCompress = (currentBlob: Blob, currentQual: number) => {
              if (currentBlob.size <= maxSizeMB * 1024 * 1024 || currentQual <= 0.1) {
                // Cria um novo File com o blob comprimido
                const compressedFile = new File([currentBlob], file.name, {
                  type: file.type,
                  lastModified: Date.now(),
                })
                resolve(compressedFile)
                return
              }

              // Reduz a qualidade e tenta novamente
              currentQual -= 0.1
              canvas.toBlob(
                (newBlob) => {
                  if (newBlob) {
                    tryCompress(newBlob, currentQual)
                  } else {
                    reject(new Error('Erro ao comprimir a imagem'))
                  }
                },
                file.type,
                currentQual
              )
            }

            tryCompress(blob, currentQuality)
          },
          file.type,
          quality
        )
      } catch (error) {
        reject(error)
      }
    }

    img.onerror = () => {
      reject(new Error('Erro ao carregar a imagem'))
    }

    // Carrega a imagem
    const reader = new FileReader()
    reader.onload = (e) => {
      if (e.target?.result) {
        img.src = e.target.result as string
      }
    }
    reader.onerror = () => {
      reject(new Error('Erro ao ler o arquivo'))
    }
    reader.readAsDataURL(file)
  })
}

/**
 * Formata o tamanho do arquivo em formato legível
 * @param bytes - Tamanho em bytes
 * @returns String formatada (ex: "2.5 MB")
 */
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 Bytes'
  
  const k = 1024
  const sizes = ['Bytes', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
}

/**
 * Verifica se o arquivo é uma imagem
 * @param file - Arquivo a ser verificado
 * @returns boolean
 */
export function isImageFile(file: File): boolean {
  return file.type.startsWith('image/')
}
